import type { ThemeJson } from '@apps/card/routes/CardCustomizer';
import { StationeryDraftFormat } from '@graphql/generated-base';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import { InteractiveCard } from '../../../GalleryTile/InteractiveCard';
import { Flex } from '@withjoy/joykit';
import { getDimensions } from '@apps/card/utils';
import type { PageJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';

type InteractiveCardPreviewProps = {
  cardJson: ThemeJson;
  cardFront: PageJson;
  padding?: number;
};

export const InteractiveCardPreview = (props: InteractiveCardPreviewProps) => {
  const { cardJson, cardFront, padding = 25 } = props;

  const revealContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const container = revealContainerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = createSafeResizeObserver(() => {
      if (container) {
        const width = container.getBoundingClientRect().width;
        const height = container.getBoundingClientRect().height;
        setContainerWidth(width);
        setContainerHeight(height);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  const dimensions = getDimensions(cardJson.card);
  const cardSizeX = dimensions.width;
  const cardSizeY = dimensions.height;
  const cardAspectRatio = cardSizeY / cardSizeX;
  const shape = cardJson.customizations.shape;

  let cardWidth = (containerWidth ?? 0) - padding;
  let cardHeight = cardWidth * cardAspectRatio;

  if (cardHeight > cardWidth) {
    cardHeight = (containerHeight ?? 0) - padding;
    cardWidth = cardHeight / cardAspectRatio;
  }

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      width="100%"
      height="100%"
      position="relative"
      marginX="auto"
      marginY="auto"
      ref={revealContainerRef}
    >
      <InteractiveCard
        width={cardWidth}
        height={cardHeight}
        format={StationeryDraftFormat.digital}
        page={cardFront}
        shape={shape}
        forcePlaceholderQRCode
      />
    </Flex>
  );
};
